// ============================================================================
// ARBITRATOR HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { arbitratorService } from '../../api/services/arbitratorService';
import type {
  Arbitrator,
  ArbitratorListItem,
  CreateArbitratorRequest,
  UpdateArbitratorRequest,
  ArbitratorSearchRequest,
} from '../../api/types/arbitrator.types';
import type { ID } from '../../api/types';

export const useArbitrators = (params?: ArbitratorSearchRequest) => {
  const [data, setData] = useState<ArbitratorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1, pageSize: 20, totalPages: 0, totalItems: 0,
    hasNextPage: false, hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await arbitratorService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { data, loading, error, pagination, refetch: fetchItems };
};

export const useArbitrator = (id: ID | undefined) => {
  const [data, setData] = useState<Arbitrator | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchItem = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const response = await arbitratorService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  return { data, loading, error, refetch: fetchItem };
};

export const useCreateArbitrator = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createArbitrator = async (
    data: CreateArbitratorRequest,
    options?: { onSuccess?: (item: Arbitrator) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await arbitratorService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createArbitrator, loading, error };
};

export const useUpdateArbitrator = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateArbitrator = async (
    id: ID, data: UpdateArbitratorRequest,
    options?: { onSuccess?: (item: Arbitrator) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await arbitratorService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateArbitrator, loading, error };
};

export const useDeleteArbitrator = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteArbitrator = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await arbitratorService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteArbitrator, loading, error };
};
